import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { 
  Card, 
  Button, 
  Input, 
  Label, 
  FormGroup, 
  FormHelp,
  Select,
  Checkbox,
  Alert
} from '../styles/components';
import { 
  UserCircleIcon,
  KeyIcon,
  BellIcon,
  ShieldCheckIcon,
  ComputerDesktopIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  max-width: 800px;
`;

const SettingsHeader = styled.div``;

const SettingsTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const SettingsSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

const SettingsNav = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
  overflow-x: auto;
`;

const NavItem = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.active ? props.theme.colors.primary[600] : props.theme.colors.gray[500]};
  border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary[600] : 'transparent'};
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.colors.primary[600]};
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const SectionCard = styled(Card)`
  padding: ${props => props.theme.spacing.xl};
`;

const SectionHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
`;

const SectionDescription = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.lg};
  
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CheckboxLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[700]};
  cursor: pointer;
`;

const SaveButton = styled(Button)`
  align-self: flex-start;
`;

const DangerZone = styled.div`
  border: 1px solid ${props => props.theme.colors.error[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.error[50]};
`;

const DangerTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.error[700]};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const DangerDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.error[600]};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const DangerButton = styled(Button).attrs({ variant: 'outline' })`
  color: ${props => props.theme.colors.error[600]};
  border-color: ${props => props.theme.colors.error[600]};
  
  &:hover {
    background-color: ${props => props.theme.colors.error[50]};
  }
`;

type SettingsTab = 'profile' | 'security' | 'notifications' | 'preferences';

export function Settings() {
  const { user } = useAuth();
  const { isSupport } = usePermissions();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    organizationName: user?.organizationName || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    emailNotifications: true,
    pushNotifications: false,
    securityAlerts: true,
    theme: 'light' as 'light' | 'dark',
    language: 'en',
    timezone: 'UTC',
  });

  const handleSave = () => {
    // Simulate save
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const tabs = [
    { id: 'profile' as SettingsTab, label: 'Profile', icon: UserCircleIcon },
    { id: 'security' as SettingsTab, label: 'Security', icon: ShieldCheckIcon },
    { id: 'notifications' as SettingsTab, label: 'Notifications', icon: BellIcon },
    { id: 'preferences' as SettingsTab, label: 'Preferences', icon: ComputerDesktopIcon },
  ];

  return (
    <SettingsContainer>
      <SettingsHeader>
        <SettingsTitle>Settings</SettingsTitle>
        <SettingsSubtitle>
          Manage your account settings and preferences
        </SettingsSubtitle>
      </SettingsHeader>

      {showSuccess && (
        <Alert variant="success">
          <CheckIcon style={{ width: '1rem', height: '1rem' }} />
          Settings saved successfully!
        </Alert>
      )}

      <SettingsNav>
        {tabs.map((tab) => (
          <NavItem
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon />
            {tab.label}
          </NavItem>
        ))}
      </SettingsNav>

      <SettingsContent>
        {activeTab === 'profile' && (
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Profile Information</SectionTitle>
              <SectionDescription>
                Update your account profile information and organization details.
              </SectionDescription>
            </SectionHeader>
            
            <FormRow>
              <FormGroup>
                <Label>Display Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your display name"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label>Organization Name</Label>
              <Input
                value={formData.organizationName}
                onChange={(e) => handleInputChange('organizationName', e.target.value)}
                placeholder="Enter organization name"
                disabled={!isSupport}
              />
              {!isSupport && (
                <FormHelp>
                  Contact support to change your organization details.
                </FormHelp>
              )}
            </FormGroup>
            
            <SaveButton onClick={handleSave}>
              Save Changes
            </SaveButton>
          </SectionCard>
        )}

        {activeTab === 'security' && (
          <>
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Change Password</SectionTitle>
                <SectionDescription>
                  Update your password to keep your account secure.
                </SectionDescription>
              </SectionHeader>
              
              <FormGroup>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                  placeholder="Enter current password"
                />
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    placeholder="Enter new password"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Confirm Password</Label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirm new password"
                  />
                </FormGroup>
              </FormRow>
              
              <SaveButton onClick={handleSave}>
                Update Password
              </SaveButton>
            </SectionCard>

            <SectionCard>
              <DangerZone>
                <DangerTitle>Danger Zone</DangerTitle>
                <DangerDescription>
                  Once you delete your account, there is no going back. Please be certain.
                </DangerDescription>
                <DangerButton>
                  Delete Account
                </DangerButton>
              </DangerZone>
            </SectionCard>
          </>
        )}

        {activeTab === 'notifications' && (
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Notification Preferences</SectionTitle>
              <SectionDescription>
                Choose how you want to be notified about account activity.
              </SectionDescription>
            </SectionHeader>
            
            <CheckboxGroup>
              <Checkbox
                checked={formData.emailNotifications}
                onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
              />
              <CheckboxLabel>Email Notifications</CheckboxLabel>
            </CheckboxGroup>
            
            <CheckboxGroup>
              <Checkbox
                checked={formData.pushNotifications}
                onChange={(e) => handleInputChange('pushNotifications', e.target.checked)}
              />
              <CheckboxLabel>Push Notifications</CheckboxLabel>
            </CheckboxGroup>
            
            <CheckboxGroup>
              <Checkbox
                checked={formData.securityAlerts}
                onChange={(e) => handleInputChange('securityAlerts', e.target.checked)}
              />
              <CheckboxLabel>Security Alerts</CheckboxLabel>
            </CheckboxGroup>
            
            <SaveButton onClick={handleSave}>
              Save Preferences
            </SaveButton>
          </SectionCard>
        )}

        {activeTab === 'preferences' && (
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Application Preferences</SectionTitle>
              <SectionDescription>
                Customize your application experience.
              </SectionDescription>
            </SectionHeader>
            
            <FormRow>
              <FormGroup>
                <Label>Theme</Label>
                <Select
                  value={formData.theme}
                  onChange={(e) => handleInputChange('theme', e.target.value)}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Language</Label>
                <Select
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </Select>
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label>Timezone</Label>
              <Select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </Select>
            </FormGroup>
            
            <SaveButton onClick={handleSave}>
              Save Preferences
            </SaveButton>
          </SectionCard>
        )}
      </SettingsContent>
    </SettingsContainer>
  );
}